import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAdAccount } from "../lib/adAccount";
import { CheckCircle, Warning, Plug, CurrencyCircleDollar, ArrowsClockwise, ShieldCheck, ShieldWarning } from "@phosphor-icons/react";

export default function Settings() {
  const { accounts } = useAdAccount();
  const [meta, setMeta] = useState(null);
  const [billingConfigs, setBillingConfigs] = useState([]);
  const [defaultPageId, setDefaultPageId] = useState("");
  const [savingPage, setSavingPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingBilling, setSavingBilling] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [m, b, p] = await Promise.all([
        api.get("/meta/status"),
        api.get("/billing/configs"),
        api.get("/meta/default-page"),
      ]);
      setMeta(m.data);
      setBillingConfigs(b.data || []);
      setDefaultPageId(p.data?.page_id || "");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const triggerSync = async () => {
    setSyncing(true); setMsg("");
    try {
      const r = await api.post("/meta/sync");
      const res = r.data?.result || {};
      setMsg(`Synced: ${res.campaigns} campaigns, ${res.ad_sets} ad sets, ${res.ads} ads, ${res.insights + res.ad_insights} insight rows, ${res.adset_breakdowns} breakdowns`);
      await load();
    } catch (e) {
      setMsg("Sync failed: " + (e.response?.data?.detail || e.message));
    } finally { setSyncing(false); }
  };

  const saveBilling = async () => {
    setSavingBilling(true); setMsg("");
    try {
      await Promise.all(billingConfigs.map((cfg) =>
        api.post("/billing/configs", {
          ad_account_id: cfg.ad_account_id,
          credit_limit: cfg.credit_limit,
          billing_cycle_start_day: cfg.billing_cycle_start_day,
        })
      ));
      setMsg("Billing settings saved");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg("Error: " + (e.response?.data?.detail || e.message));
    } finally { setSavingBilling(false); }
  };

  const saveDefaultPage = async () => {
    setSavingPage(true); setMsg("");
    try {
      await api.post("/meta/default-page", { page_id: defaultPageId.trim() });
      setMsg("Default Page ID saved");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg("Error: " + (e.response?.data?.detail || e.message));
    } finally { setSavingPage(false); }
  };

  if (loading || !meta) return <div className="p-6 text-zinc-500">Loading...</div>;

  const tk = meta.token || {};
  const acc = meta.account || {};
  const lastSync = meta.last_sync;
  const tokenValid = tk.valid;
  const tokenNeverExpires = tk.expires_at === 0;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-zinc-900 px-6 flex items-center backdrop-blur-xl bg-zinc-950/70 sticky top-0 z-10">
        <div>
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Settings</div>
          <div className="text-sm font-medium">Meta API & Billing</div>
        </div>
      </div>

      <div className="p-6 max-w-3xl">
        {/* Meta Connection Status */}
        <section className="mb-8">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-2">// Meta Marketing API</div>
          <h2 className="font-display text-xl font-semibold mb-4">Connection Status</h2>

          {!meta.configured ? (
            <div className="bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-xs text-amber-400 flex items-start gap-2">
              <Warning size={16} className="shrink-0 mt-0.5" />
              <div>
                Meta credentials not configured. Add <code className="font-mono">META_APP_ID</code>, <code className="font-mono">META_APP_SECRET</code>, <code className="font-mono">META_ACCESS_TOKEN</code>, <code className="font-mono">META_AD_ACCOUNT_ID</code> to backend <code className="font-mono">.env</code> and restart.
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900/40 border border-zinc-800/60 p-5 space-y-4" data-testid="meta-status-card">
              {/* Token */}
              <div className="flex items-start gap-3">
                {tokenValid ? <ShieldCheck size={18} className="text-emerald-400 shrink-0 mt-0.5" weight="fill" /> : <ShieldWarning size={18} className="text-red-400 shrink-0 mt-0.5" weight="fill" />}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium">Access Token</span>
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 ${tokenValid ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
                      {tokenValid ? "Valid" : "Invalid"}
                    </span>
                    {tokenNeverExpires && tokenValid && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-blue-500/10 text-blue-400">Never Expires</span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    Type: {tk.type || "—"} · App: {tk.app || "—"}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    Scopes: {(tk.scopes || []).join(", ") || "—"}
                  </div>
                </div>
              </div>

              {/* Ad Account */}
              <div className="border-t border-zinc-800 pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Plug size={14} className="text-orange-500" />
                  <span className="text-sm font-medium">Connected Ad Account</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-[11px] font-mono text-zinc-400">
                  <div><span className="text-zinc-600">Name:</span> {acc.name || "—"}</div>
                  <div><span className="text-zinc-600">Currency:</span> {acc.currency || "—"}</div>
                  <div><span className="text-zinc-600">ID:</span> {acc.id || "—"}</div>
                  <div><span className="text-zinc-600">Timezone:</span> {acc.timezone || "—"}</div>
                  <div><span className="text-zinc-600">Business:</span> {acc.business_name || "—"}</div>
                  <div><span className="text-zinc-600">Status:</span> {acc.status === 1 ? "Active" : acc.status || "—"}</div>
                </div>
              </div>

              {/* Sync */}
              <div className="border-t border-zinc-800 pt-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium mb-0.5">Data Sync</div>
                    <div className="text-[11px] text-zinc-500 font-mono">
                      {lastSync?.synced_at ? `Last sync: ${new Date(lastSync.synced_at).toLocaleString()}` : "Never synced"}
                    </div>
                    {lastSync?.result && (
                      <div className="text-[11px] text-zinc-600 font-mono mt-0.5">
                        {lastSync.result.campaigns} campaigns · {lastSync.result.ads} ads · {lastSync.result.insights + (lastSync.result.ad_insights||0)} insights
                      </div>
                    )}
                  </div>
                  <button
                    data-testid="meta-sync-button"
                    onClick={triggerSync}
                    disabled={syncing || !tokenValid}
                    className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-zinc-950 font-semibold text-xs px-4 py-2 transition-colors flex items-center gap-2 shrink-0"
                  >
                    <ArrowsClockwise size={13} weight="bold" className={syncing ? "animate-spin" : ""} />
                    {syncing ? "Syncing..." : "Sync Now"}
                  </button>
                </div>
                <div className="text-[10px] text-zinc-600 font-mono mt-2">
                  Pulls last 30 days of data from Meta. Takes 30-60 seconds.
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Default FB Page */}
        <section className="mb-8">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-2">// Boost Existing Post</div>
          <h2 className="font-display text-xl font-semibold mb-1">Default Facebook Page</h2>
          <p className="text-xs text-zinc-500 mb-4">Set the FB Page ID used when AI lists/boosts existing organic posts as paid ads. Find it at <span className="font-mono text-zinc-400">facebook.com/yourpage</span> → About → Page transparency → Page ID.</p>
          <div className="bg-zinc-900/40 border border-zinc-800/60 p-4">
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Page ID</label>
            <div className="flex gap-2">
              <input
                data-testid="default-page-id-input"
                type="text"
                value={defaultPageId}
                onChange={(e) => setDefaultPageId(e.target.value)}
                placeholder="e.g. 122099204127304878"
                className="flex-1 bg-[#050505] border border-zinc-800 px-3 py-2 text-sm font-mono focus:outline-none focus:border-orange-500"
              />
              <button
                data-testid="save-default-page-button"
                onClick={saveDefaultPage}
                disabled={savingPage || !defaultPageId.trim()}
                className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-zinc-950 font-semibold text-sm px-5 transition-colors"
              >
                {savingPage ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </section>
        <section className="mb-8">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-2">// Payment Alerts</div>
          <h2 className="font-display text-xl font-semibold mb-1">Billing per Ad Account</h2>
          <p className="text-xs text-zinc-500 mb-4">Set credit limit & cycle start day. Alerts trigger at 30% / 15% / 5% remaining.</p>
          <div className="space-y-2">
            {accounts.map((acct) => {
              const cfg = billingConfigs.find(c => c.ad_account_id === acct.id) || { ad_account_id: acct.id, credit_limit: 0, billing_cycle_start_day: 1 };
              const updateLocal = (field, val) => {
                setBillingConfigs((bc) => {
                  const next = bc.filter(c => c.ad_account_id !== acct.id);
                  return [...next, { ...cfg, [field]: val }];
                });
              };
              return (
                <div key={acct.id} className="bg-zinc-900/40 border border-zinc-800/60 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CurrencyCircleDollar size={14} className="text-orange-500" />
                    <div className="font-medium text-sm">{acct.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{acct.id}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Credit Limit (RM)</label>
                      <input
                        data-testid={`credit-limit-${acct.id}`}
                        type="number"
                        min="0"
                        step="100"
                        value={cfg.credit_limit || ""}
                        onChange={(e) => updateLocal("credit_limit", parseFloat(e.target.value) || 0)}
                        className="w-full bg-[#050505] border border-zinc-800 px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:border-orange-500"
                        placeholder="30000"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Cycle Start (Day of Month)</label>
                      <input
                        data-testid={`cycle-start-${acct.id}`}
                        type="number"
                        min="1"
                        max="28"
                        value={cfg.billing_cycle_start_day || 1}
                        onChange={(e) => updateLocal("billing_cycle_start_day", parseInt(e.target.value) || 1)}
                        className="w-full bg-[#050505] border border-zinc-800 px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:border-orange-500"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="flex items-center gap-3">
          <button
            data-testid="billing-save-button"
            onClick={saveBilling}
            disabled={savingBilling}
            className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-100 font-medium text-sm px-5 py-2 transition-colors flex items-center gap-2"
          >
            <CurrencyCircleDollar size={14} weight="fill" />
            {savingBilling ? "Saving..." : "Save Billing Settings"}
          </button>
          {msg && (
            <span className={`text-xs flex items-center gap-1.5 ${msg.toLowerCase().includes("error") || msg.toLowerCase().includes("failed") ? "text-red-400" : "text-emerald-400"}`}>
              <CheckCircle size={13} weight="fill" /> {msg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
